import { useCallback, useEffect, useMemo, useRef } from 'react';

import debounce from 'lodash/debounce';

import { useDeviceTypes } from '@travel/traveler-core/hooks';

import { setViewCount } from 'store/viewCount/actions';

import {
  PC_VIEW_COUNT_THRESHOLD,
  SP_VIEW_COUNT_THRESHOLD,
  VIEW_COUNT_DEBOUNCE_DELAY,
  VIEW_COUNT_ID_LIMIT,
} from 'constants/common';
import { useAppDispatch } from 'store/hooks';

import type { Review } from 'store/apiTypes';
type Props = {
  delay?: number;
  targetRef: React.RefObject<Array<Element | null>>;
  threshold?: number;
  isDisabled?: boolean;
  contents?: Review[];
};

export default function useViewCount(props: Props) {
  const { isPC } = useDeviceTypes();

  const {
    targetRef,
    contents,
    delay = VIEW_COUNT_DEBOUNCE_DELAY,
    threshold = isPC ? PC_VIEW_COUNT_THRESHOLD : SP_VIEW_COUNT_THRESHOLD,
    isDisabled = false,
  } = props;
  const viewIds = useRef<number[]>([]);
  const dispatch = useAppDispatch();
  const debouncedSetViewCount = useRef<ReturnType<typeof debounce>>();

  useEffect(() => {
    debouncedSetViewCount.current = debounce(() => {
      dispatch(setViewCount(viewIds.current));
      viewIds.current = [];
    }, delay);

    return () => {
      debouncedSetViewCount.current?.cancel();
    };
  }, [delay, dispatch]);
  const handleIntersection = useCallback(
    (entries) => {
      entries.map((entry) => {
        if (entry.isIntersecting) {
          const id = parseInt(entry.target?.dataset.id);

          if (!viewIds.current.includes(id)) {
            viewIds.current.push(id);

            if (viewIds.current.length > VIEW_COUNT_ID_LIMIT) {
              dispatch(setViewCount(viewIds.current));
              viewIds.current = [];
              debouncedSetViewCount.current?.cancel();
            } else {
              debouncedSetViewCount.current && debouncedSetViewCount.current();
            }
          }
        }
      });
    },
    [dispatch],
  );
  const observer = useMemo(
    () =>
      typeof window !== 'undefined' && 'IntersectionObserver' in window
        ? new IntersectionObserver(handleIntersection, { threshold })
        : undefined,
    [handleIntersection, threshold],
  );

  useEffect(() => {
    if (isDisabled) return;

    if (targetRef.current) {
      targetRef.current.forEach((ref) => {
        if (ref) observer?.observe(ref);
      });
    }
  }, [contents, isDisabled, targetRef, observer]);
  useEffect(() => {
    return () => {
      observer?.disconnect();
      if (viewIds.current.length) dispatch(setViewCount(viewIds.current));
      debouncedSetViewCount.current?.cancel();
    };
  }, [dispatch, observer]);
}
